import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Trash2, X, Layers, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { inventoryService } from '@/services/inventory';
import { fmt } from '@/lib/format';
import type { BundleComponentRow, InventoryItem } from '@/types/inventory';

interface Props {
  item: InventoryItem | null;
}

/**
 * Bundle components — inline editor for the "this bundle is made of N
 * pieces of A and M of B" rule. Lives inside the inventory detail dialog
 * and is only rendered when the item has `isBundle=true`.
 *
 * The actual stock expansion at sale/dispatch time lands in Slice 12b;
 * this section establishes the definition that expansion will read.
 */
export function ItemBundleComponentsSection({ item }: Props) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);

  const itemId = item?.id ?? null;

  const componentsQuery = useQuery({
    queryKey: ['inventory', 'bundle-components', itemId],
    queryFn: () => inventoryService.listBundleComponents(itemId!),
    enabled: !!itemId && !!item?.isBundle,
  });

  const remove = useMutation({
    mutationFn: (componentRowId: string) =>
      inventoryService.removeBundleComponent(itemId!, componentRowId),
    onSuccess: () => {
      toast.success('Component removed');
      qc.invalidateQueries({ queryKey: ['inventory', 'bundle-components', itemId] });
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not remove component'),
  });

  const updateQty = useMutation({
    mutationFn: ({ componentRowId, quantity }: { componentRowId: string; quantity: number }) =>
      inventoryService.updateBundleComponent(itemId!, componentRowId, { quantity }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inventory', 'bundle-components', itemId] });
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not update quantity'),
  });

  if (!item) return null;

  if (!item.isBundle) {
    return (
      <div className="rounded-md border border-info/20 bg-info/5 px-3 py-3 text-[12.5px]">
        <span className="font-medium">This item is not a bundle.</span>{' '}
        <span className="text-muted-foreground">
          Edit the item and enable "This is a bundle" to define components.
        </span>
      </div>
    );
  }

  const components = componentsQuery.data ?? [];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-[11px] text-muted-foreground">
          Selling 1 unit of <span className="font-mono text-foreground">{item.sku}</span>{' '}
          consumes the components below.
        </div>
        <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
          <Plus className="h-3 w-3" />
          Add component
        </Button>
      </div>

      <div className="flex items-center gap-2 rounded-md border border-warning/20 bg-warning/5 px-3 py-2 text-[11.5px] text-[hsl(38_85%_30%)]">
        <AlertCircle className="h-3.5 w-3.5 shrink-0" />
        <span>
          <span className="font-medium">Definition only.</span> Reservation, dispatch and
          return expansion to components ships in Slice 12b — until then, sales orders
          treat bundles as plain SKUs.
        </span>
      </div>

      {adding && (
        <AddComponentForm
          bundleItemId={item.id}
          onClose={() => setAdding(false)}
          excludeIds={[item.id, ...components.map((c) => c.componentItemId)]}
        />
      )}

      {componentsQuery.isLoading ? (
        <div className="text-[12px] text-muted-foreground">Loading…</div>
      ) : components.length === 0 ? (
        <div className="rounded-md border border-dashed border-border/70 px-3 py-6 text-center text-[12.5px] text-muted-foreground">
          No components yet. Add the items this bundle is made of.
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border/70">
          <div className="grid grid-cols-12 gap-3 border-b border-border/60 bg-muted/40 px-4 py-2 text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
            <div className="col-span-7">Component item</div>
            <div className="col-span-3 text-right">Qty per bundle</div>
            <div className="col-span-2" />
          </div>
          {components.map((c) => (
            <ComponentRow
              key={c.id}
              row={c}
              onQuantityChange={(quantity) =>
                updateQty.mutate({ componentRowId: c.id, quantity })
              }
              onDelete={() => remove.mutate(c.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ComponentRow({
  row,
  onQuantityChange,
  onDelete,
}: {
  row: BundleComponentRow;
  onQuantityChange: (quantity: number) => void;
  onDelete: () => void;
}) {
  const [draftQty, setDraftQty] = useState(String(Number(row.quantity)));
  const dirty = draftQty !== String(Number(row.quantity));

  return (
    <div className="grid grid-cols-12 items-center gap-3 border-b border-border/60 px-4 py-2.5 text-[12.5px] last:border-0">
      <div className="col-span-7 flex items-center gap-2.5">
        <span aria-hidden="true" className="grid h-7 w-7 shrink-0 place-items-center rounded-md bg-muted text-muted-foreground">
          <Layers className="h-3.5 w-3.5" />
        </span>
        <div className="min-w-0">
          <div className="truncate font-medium text-foreground">{row.component.name}</div>
          <div className="font-mono text-[10.5px] text-muted-foreground">
            {row.component.sku} · base unit{' '}
            <span className="font-mono">{row.component.unitOfMeasure}</span>
          </div>
        </div>
      </div>
      <div className="col-span-3 flex items-center justify-end gap-2">
        <Input
          type="number"
          inputMode="decimal"
          min={0}
          step="0.001"
          value={draftQty}
          onChange={(e) => setDraftQty(e.target.value)}
          onBlur={() => {
            const n = Number(draftQty);
            if (Number.isFinite(n) && n > 0 && dirty) onQuantityChange(n);
            else setDraftQty(String(Number(row.quantity)));
          }}
          className="h-7 w-20 text-right font-mono text-[12px] tabular-nums"
        />
        <Badge variant="muted" className="font-mono text-[10px]">
          {row.component.unitOfMeasure}
        </Badge>
      </div>
      <div className="col-span-2 flex justify-end">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 text-muted-foreground hover:text-destructive"
              onClick={onDelete}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Remove component</TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
}

function AddComponentForm({
  bundleItemId,
  excludeIds,
  onClose,
}: {
  bundleItemId: string;
  excludeIds: string[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [componentItemId, setComponentItemId] = useState('');
  const [quantity, setQuantity] = useState('');

  // Pull all active items so the operator can pick a component. Filter
  // out: the bundle itself + any item already in this bundle's components
  // + any nested bundles (BE rejects but we hide them too).
  const itemsQuery = useQuery({
    queryKey: ['inventory', 'items', { status: 'ACTIVE', forBundle: true }],
    queryFn: () => inventoryService.list({ status: 'ACTIVE', pageSize: 200 }),
    staleTime: 30_000,
  });
  const eligible =
    (itemsQuery.data?.items ?? []).filter(
      (it) => !excludeIds.includes(it.id) && !it.isBundle,
    );

  const create = useMutation({
    mutationFn: () =>
      inventoryService.addBundleComponent(bundleItemId, {
        componentItemId,
        quantity: Number(quantity),
      }),
    onSuccess: () => {
      toast.success('Component added');
      qc.invalidateQueries({ queryKey: ['inventory', 'bundle-components', bundleItemId] });
      onClose();
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not add component'),
  });

  const valid = componentItemId && Number(quantity) > 0;

  return (
    <div className="rounded-md border border-border/70 bg-card p-3.5">
      <div className="mb-3 flex items-center justify-between">
        <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
          Add component
        </div>
        <Button size="icon" variant="ghost" className="h-6 w-6" onClick={onClose}>
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>

      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-8">
          <label className="text-[11px] text-muted-foreground" htmlFor="componentItemId">
            Component item
          </label>
          <Select value={componentItemId} onValueChange={setComponentItemId}>
            <SelectTrigger id="componentItemId" className="mt-1 h-8 text-[12.5px]">
              <SelectValue placeholder={itemsQuery.isLoading ? 'Loading…' : 'Pick a SKU'} />
            </SelectTrigger>
            <SelectContent>
              {eligible.length === 0 ? (
                <SelectItem value="__none" disabled>
                  No eligible items
                </SelectItem>
              ) : (
                eligible.map((it) => (
                  <SelectItem key={it.id} value={it.id}>
                    <span className="font-mono text-[11px] text-muted-foreground">{it.sku}</span>
                    <span className="ml-2">{it.name}</span>
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </div>
        <div className="col-span-4">
          <label className="text-[11px] text-muted-foreground" htmlFor="qty">
            Qty per bundle
          </label>
          <Input
            id="qty"
            type="number"
            inputMode="decimal"
            min={0}
            step="0.001"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            placeholder="0"
            className="mt-1 h-8 text-right font-mono text-[12.5px] tabular-nums"
          />
          <div className="mt-1 text-[10.5px] text-muted-foreground">
            In the component's BASE unit.
          </div>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button size="sm" onClick={() => create.mutate()} disabled={!valid || create.isPending}>
          {create.isPending ? 'Adding…' : `Add ${fmt.number(Number(quantity) || 0)} ×`}
        </Button>
      </div>
    </div>
  );
}
